/**
 * Estilos centralizados para UI/UX consistente
 * Formularios densos de operación — diseño sobrio y elegante
 */

// Badge Styles
export const badgeStyles = {
  // Badge neutro (gris)
  neutral:
    "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",

  // Badges de estado
  status: {
    active:
      "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
    inactive:
      "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
    cancelled:
      "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
    completed:
      "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
    inTransit:
      "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
    pending:
      "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
    preparation:
      "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
    violet:
      "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800",
  },

  // Badge de contador/número
  counter:
    "inline-flex items-center justify-center rounded px-2 py-0.5 text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200 min-w-[24px] dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",

  // Badge de tipo/movimiento
  type: "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
} as const;

// Text Styles
export const textStyles = {
  // Texto principal (negro/gris oscuro)
  primary: "text-foreground text-xs",
  primaryMedium: "text-foreground font-medium text-xs",
  primaryBold: "text-foreground font-semibold text-xs",

  // Texto secundario (gris medio)
  secondary: "text-muted-foreground text-xs",
  secondaryMedium: "text-muted-foreground font-medium text-xs",

  // Texto terciario (gris claro)
  tertiary: "text-muted-foreground/80 text-xs",
  tertiaryMedium: "text-muted-foreground/80 font-medium text-xs",

  // Texto mutado (gris muy claro)
  muted: "text-muted-foreground/60 text-xs",

  // Texto deshabilitado
  disabled: "text-muted-foreground/40 text-xs",
} as const;

// Table Styles
export const tableStyles = {
  // Container
  container: "border rounded-lg overflow-x-auto bg-card",

  // Header
  header: {
    row: "h-9 border-b",
    cell: "px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50",
    cellSortable:
      "px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 cursor-pointer hover:bg-muted select-none",
  },

  // Body
  body: {
    row: "h-8 border-b hover:bg-muted/50",
    rowClickable: "h-8 border-b hover:bg-muted/50 cursor-pointer",
    cell: "px-2 py-1.5 text-xs",
    cellEmpty: "text-center py-6 text-xs text-muted-foreground",
  },

  // Pagination
  pagination: {
    container:
      "flex items-center justify-between py-2 px-1 border-t bg-muted/20",
    info: "text-xs text-muted-foreground",
    button: "h-7 px-2 text-xs",
    buttonNumber: "h-7 w-7 p-0 text-xs",
  },
} as const;

// Page Layout Styles
export const pageLayoutStyles = {
  // Main container
  main: "flex-1 overflow-y-auto p-3",

  // Card container
  card: "w-full",
  cardComponent: "border-0 shadow-sm bg-card text-card-foreground",

  // Card header
  cardHeader: "pb-3 px-4 pt-4",
  cardTitle: "text-lg font-semibold",

  // Card content
  cardContent: "px-4 pb-4 pt-0",

  // Search input
  search: {
    container: "mb-2",
    input: "pl-8 h-8 text-xs",
    icon: "absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground w-3.5 h-3.5",
  },

  // Action buttons
  actionButton: "h-8 text-xs",
  actionButtonIcon: "w-3.5 h-3.5",

  // Table action buttons
  tableActionButton: "h-6 w-6 p-0",
  tableActionIcon: "w-3 h-3",
} as const;

// Form Styles
export const formStyles = {
  // Container
  container: "bg-card text-card-foreground border rounded-lg overflow-hidden",
  pageBody: "px-3 py-4 sm:px-12 sm:py-8 space-y-0",
  pageHeader:
    "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-lg border bg-muted/20 px-3 py-3 sm:px-10 sm:py-5",
  pageSection:
    "grid grid-cols-1 lg:grid-cols-12 gap-5 py-5 sm:gap-8 sm:py-8 border-b border-border last-of-type:border-b-0 last-of-type:pb-0 first:pt-2",
  pageSectionAside: "lg:col-span-4 space-y-1.5",
  pageSectionContent: "lg:col-span-8",
  pageSectionTitle: "text-sm font-semibold text-foreground",
  pageSectionDescription: "text-xs text-muted-foreground leading-relaxed",
  fieldGrid:
    "flex flex-wrap items-start gap-x-3 gap-y-5 sm:gap-x-10 sm:gap-y-6 [&>*]:w-full sm:[&>*]:w-auto",
  /** Columna estrecha (modal/dialog): stack vertical, 100% ancho contenedor */
  fieldGridModal:
    "field-grid-modal flex flex-col gap-5 sm:gap-6 w-full min-w-0 [&>*]:w-full",
  /** Ver FIELD_WIDTH_PRESETS en lib/form-field-width.ts — usar prop fieldSize en Input/SelectField */
  fieldSize: {
    xs: { min: 14, max: 22 },
    sm: { min: 18, max: 30 },
    md: { min: 20, max: 38 },
    lg: { min: 24, max: 48 },
    full: { min: 28, max: 64 },
  },
  fieldWidth: {
    auto: "w-fit max-w-full",
    full: "w-full max-w-[64ch]",
  },
  actions:
    "flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-6 mt-2 border-t border-border",

  // Input — border explícito + sombra sutil en light para delimitar el control
  input:
    "h-9 w-full rounded-md border border-input text-xs text-foreground shadow-xs dark:shadow-none focus:border-primary focus:ring-1 focus:ring-primary px-3 bg-card dark:bg-input/30 placeholder:text-muted-foreground",
  inputLarge:
    "h-8 text-xs border border-input shadow-xs dark:shadow-none focus:border-primary focus:ring-1 focus:ring-primary px-3 bg-background",

  // Label
  label: "text-xs font-medium text-foreground whitespace-nowrap",
  labelInline:
    "text-xs font-medium text-foreground whitespace-nowrap min-w-[100px]",

  // Select
  select:
    "h-9 text-xs border border-input bg-background shadow-xs dark:shadow-none",

  // Button
  button: {
    sm: "h-7 text-xs px-2",
    md: "h-8 text-sm px-3",
    lg: "h-9 text-base px-4",
  },

  // Form Grid
  grid: {
    container:
      "field-grid-page grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 [&>*]:min-w-0",
    container2Cols: "field-grid-page grid grid-cols-1 md:grid-cols-2 gap-3",
    container3Cols:
      "field-grid-page grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3",
    container4Cols:
      "field-grid-page grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3",
    fullWidth: "col-span-1 md:col-span-2 lg:col-span-3",
    fullWidth2Cols: "col-span-1 md:col-span-2",
  },

  // Form Section
  section: {
    container: "space-y-2.5",
    title: "text-xs font-semibold text-foreground",
    description: "text-xs text-muted-foreground",
  },

  // Form Row (horizontal compact layout)
  row: {
    container: "flex items-center gap-2",
    label:
      "text-xs font-medium text-foreground whitespace-nowrap min-w-[120px]",
    field: "flex-1 min-w-0",
  },

  // Control wrapper — description/error share input width and wrap
  fieldControl: "form-field-width max-w-full space-y-1",
  fieldDescription:
    "text-xs text-muted-foreground leading-relaxed break-words [overflow-wrap:anywhere]",
  fieldError:
    "text-xs text-destructive leading-relaxed break-words [overflow-wrap:anywhere] flex items-start gap-1.5",

  // Reserved error slot — prevents layout shift when validation messages appear
  errorSlot: "px-0.5",
} as const;

/** Paneles informativos laterales (stock, tipo de movimiento, vista previa) */
export const infoPanelStyles = {
  card: "border-border bg-card text-card-foreground shadow-sm gap-0 py-0",
  cardPreview:
    "border-primary/30 bg-primary/5 dark:bg-primary/10 text-card-foreground shadow-sm gap-0 py-0",
  cardError:
    "border-destructive/30 bg-destructive/5 text-card-foreground shadow-sm gap-0 py-0",
  header: "pb-2 px-3 pt-2",
  content: "px-3 pb-2",
  title: "text-xs font-medium text-foreground",
  label: "text-xs text-muted-foreground",
  value: "text-xs font-medium text-foreground",
  emptyMessage: "text-xs text-muted-foreground",
  errorMessage: "text-xs text-destructive",
  warningBanner:
    "mb-2 rounded-md border border-amber-200 bg-amber-50 p-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  previewDivider: "mt-2 pt-2 border-t border-border",
  previewSection: "space-y-1.5",
  previewArrowWrap:
    "bg-muted border border-border rounded-full p-1.5 shadow-sm",
  previewArrowIcon: "w-4 h-4 text-primary",
  availableHighlight: "text-green-700 dark:text-green-400",
  summaryLabel: "text-xs font-medium text-foreground",
} as const;

// Helper function para obtener badge de estado
export function getStatusBadge(
  status: string | null | undefined,
  variant?: "default" | "custom",
): string {
  if (status == null || status === "") {
    return badgeStyles.neutral;
  }
  const statusLower = status.toLowerCase();

  if (variant === "custom") {
    return badgeStyles.status.inTransit;
  }

  switch (statusLower) {
    case "activo":
    case "active":
    case "completada":
    case "completed":
    case "released":
    case "liberado":
      return badgeStyles.status.active;
    case "anulado":
    case "anulada":
    case "cancelled":
    case "cancelado":
    case "blocked":
    case "bloqueado":
      return badgeStyles.status.inactive;
    case "en tránsito":
    case "en_transito":
    case "tránsito":
    case "transito":
    case "in_transit":
      return badgeStyles.status.inTransit;
    case "pendiente":
    case "pending":
    case "quarantine":
    case "cuarentena":
      return badgeStyles.status.pending;
    case "preparacion":
    case "preparación":
    case "preparation":
      return badgeStyles.status.preparation;
    case "draft":
    case "borrador":
      return badgeStyles.status.pending;
    case "confirmed":
    case "confirmada":
      return badgeStyles.status.active;
    case "settled":
    case "liquidada":
      return badgeStyles.status.active;
    default:
      return badgeStyles.neutral;
  }
}

// Helper function para formatear números
export function formatNumber(
  value: number | null | undefined,
  decimals: number = 0,
): string {
  // Manejar valores undefined, null o NaN
  if (value === null || value === undefined || isNaN(value)) {
    return "0";
  }
  return value.toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Helper function para formatear decimales (sin ceros trailing)
export function formatDecimal(
  value: number | string | null | undefined,
  maxFractionDigits: number = 6,
): string {
  if (value === null || value === undefined || value === "") return "";
  const num = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  });
}

// Helper function para formatear fechas
export function formatDate(
  date: Date | string,
  format: "short" | "long" = "short",
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year =
    format === "long" ? d.getFullYear() : d.getFullYear().toString().slice(-2);
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  if (format === "long") {
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/**
 * Formatea una fecha (ISO datetime o YYYY-MM-DD) como DD/MM/YYYY.
 * No usa Date para evitar el desfase de zona horaria de las columnas @db.Date
 * (que llegan como UTC medianoche y en hora local restan un día).
 */
export function formatDateOnly(value: string | Date | null | undefined): string {
  if (!value) return "";
  const iso = value instanceof Date ? value.toISOString() : value;
  const [year, month, day] = iso.slice(0, 10).split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
}
